/**
 * What both SWR fetchers — `pages/_app.tsx` and `app/layout.tsx` — agree a failed
 * read is, and what SWR should do about one.
 *
 * The two routers each carry their own fetcher because they reach for different
 * things around it (one dispatches redux alerts and reads `next/router`, the other
 * does neither). The error contract must not differ between them, though: a screen
 * behaves the same whichever router rendered it, so that part lives here.
 */

export interface ApiError extends Error {
    status: number;
    info?: { message?: string };
}

/**
 * Statuses that resolve with the body instead of throwing.
 *
 * Both are answered inline rather than by an error state: 401 sends the visitor to
 * the login, 403 is the upgrade card, the missing-permission notice, or a permission
 * probe that is supposed to read empty data. Turning those into thrown errors would
 * put a failure toast on top of a screen that is already saying the right thing.
 */
const RESOLVED_STATUSES = [401, 403];

/**
 * Throws when a read did not succeed, so the caller's `catch`/SWR `error` sees it.
 *
 * Resolving the error JSON as if it were data leaves every SWR hook with `data` set
 * and `error` empty, which is how a billing history that could not load rendered as
 * "Noch nichts abgerechnet" — a false statement instead of a failure.
 */
export const throwIfFailedRead = (res: Response, data: any): void => {
    if (res.ok || RESOLVED_STATUSES.includes(res.status)) return;

    throw Object.assign(new Error(data?.message || res.statusText), {
        status: res.status,
        info: data
    }) as ApiError;
};

/** Attempts after the first one, for the errors that are worth attempting again at all. */
const ERROR_RETRY_COUNT = 3;

/**
 * SWR's own default is to retry a failed read forever — `errorRetryCount` is undefined
 * out of the box and its `onErrorRetry` only stops when that number is set. Every read
 * that legitimately answers 404 or 422 would therefore re-request for as long as the
 * page stays open, which is a cost the app never paid while the fetcher was resolving
 * those bodies as data.
 *
 * So: a client error is an answer, not a hiccup, and asking again cannot change it.
 * Server errors and network failures keep their retries, bounded.
 */
export const swrRetryPolicy = {
    shouldRetryOnError: (error: Error) => {
        const status = (error as Partial<ApiError>)?.status;

        return !status || status < 400 || status >= 500;
    },
    errorRetryCount: ERROR_RETRY_COUNT
};
